import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, input, signal } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {BidInventoryItem, BuyErrors, FinanceSource, LaunchDarklyService, PurchaseActionService, multiLanguageAlphaNumericRegexHelper} from 'remarketing-angular-library';
import { OfferModalAction } from '../../../shared/directives/offer-modal-action/offer-modal-action';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Observable, Subject } from 'rxjs';
import { OfferHistory } from '../../../shared/models/offer-history.model';
import { FeeToolTipService } from '../../../shared/services/feetooltip/feetooltip.service';
import { takeUntil } from 'rxjs/operators';
import { get } from 'lodash';
import { OfferModalItem } from '../../../shared/models/offer-modal-item.model';
import {
  CharacterCountComponent,
  CharacterCountDirective, CurrencyInputComponent, DatalistItemComponent,
  InputConfig,
  ModalComponent,
  SpinnerComponent, TooltipComponent
} from 'remarketing-ui-components-lib';
import { OffersService } from '../../../shared/services/offers/offers.service';
import { ActivatedRoute } from '@angular/router';
import { OfferModalDirective } from '../offer-modals/offer-modal.directive';
import { AcceptOffer, CounterOffer, DeclineOffer, OfferModalEvent, AnalyticsService, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { OfferModalsService } from '../offer-modals/service/offer-modals.service';
import { Store } from '@ngxs/store';
import {
  OfferModalsHeaderComponent
} from '../../../shared/ui/comment/modal-actions/offer-header/offer-modals-header.component';
import { OfferHistoryComponent } from '../offer-modals/offer-history/offer-history.component';
import {
  ActionSubmittingComponent
} from '../../../shared/ui/comment/modal-actions/action-submitting/action-submitting.component';
import {
  ActionSubmittedComponent
} from '../../../shared/ui/comment/modal-actions/action-submitted/action-submitted.component';
import {
  ActionErrorComponent
} from '../../../shared/ui/comment/modal-actions/action-error/action-error.component';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-shared-list-common-action-modal',
    templateUrl: './shared-list-common-action-modal.component.html',
    styleUrls: ['./shared-list-common-action-modal.component.scss'],
    imports: [
        TranslocoDirective,
        ModalComponent,
        SpinnerComponent,
        OfferModalsHeaderComponent,
        OfferHistoryComponent,
        ReactiveFormsModule,
        CharacterCountDirective,
        CharacterCountComponent,
        DatalistItemComponent,
        TooltipComponent,
        CurrencyInputComponent,
        ActionSubmittingComponent,
        ActionSubmittedComponent,
        ActionErrorComponent,
        CommonModule,
        MatCheckboxModule
    ]
})
export class SharedListCommonActionModalComponent extends OfferModalAction implements OnInit, OnDestroy {
  @Output() modalEvent = new EventEmitter<OfferModalEvent>();
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild(OfferModalDirective) offerModalHost: OfferModalDirective;
  @Input() modalId = 'shared-list-common-action-modal';
  @Input() tenantName: string;
  @Input() item: OfferModalItem;
  @Input() bidItem: BidInventoryItem;
  @Input() financeSource: FinanceSource;
  @Input() vin: string;
  @Input() offerHistory: OfferHistory[];
  @Input() hasError = false;
  @Input() index?: number;
  /** From the bid list search response metadata; hosts that don't pass it never show the spare key UI. */
  readonly platformSpareKeyFee = input<number | undefined>(undefined);
  readonly spareKeySelected = signal(false);

  isOpen = false;
  errorText: string;
  private readonly destroyed$ = new Subject();
  declinedOffer = false;
  acceptedOffer = false;
  counteredOffer = false;
  errorModal = false;
  counterOfferError: string = null;
  waitingResponse = false;
  successfulResponse = false;
  public isLoading = true;
  textAccept: string;
  textCounter: string;
  textView: string;
  textPurchaseTotal: string;
  textDecline: string;
  textSpareKeyTotal: string;
  textAddSpareKeyFor: string;
  textSentToBuyingDealership: string;
  labelErrorTitle: string;
  labelErrorSubTitle: string;

  errorMessage: string | ((val: string | number) => string);
  form: UntypedFormGroup;
  characterCountData: any;
  rootOffer: number;
  formattedRootOffer: string;
  offerChain;
  toolTipContent: any;
  currentModalType: string;
  currentModalId: string;
  sellingDisclosureUrl: string;
  isOfferAmount: boolean;
  enterValidAmount: string;

  inputConfig: InputConfig = {
    inputClass: 'form-control modal__form-control',
    inputName: 'counterOfferAmount',
    inputFormControlName: 'counterOfferAmount',
    inputAttrAriaLabelledBy: 'counter-offer-amount-label counter-offer-amount-error',
    thousandSeparator: '',
    isFrenchCanadian: this.isFrenchCanadian
  };

  constructor(
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly translocoService: TranslocoService,
    private readonly offersService: OffersService,
    private readonly _analyticsService: AnalyticsService,
    private readonly _tooltipService: FeeToolTipService,
    private readonly _route: ActivatedRoute,
    private readonly purchaseActionService: PurchaseActionService,
    private readonly _offerModalsService: OfferModalsService,
    private readonly store: Store,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly currencyFormatService: CurrencyFormatService
  ) {
    super();
  }

  get vehicleTitle() {
    const { modelYear = '', make = '', model = '', trim = '', vin = '' } = this.item;
    return `${modelYear} ${make} ${model} ${trim} - VIN ${vin}`;
  }

  get bidItemBuyFeeTotal(): number {
    return this.bidItem?.buyFee?.total ? this.bidItem.buyFee.total : 0;
  }

  get totalPurchasePrice(): string {
    const total = this.rootOffer + this.bidItemBuyFeeTotal + this.spareKeyTotal;
    return this.formatCurrency(total, false);
  }

  /** Spare key purchase feature flag; LD targeting already scopes it to Avis US. */
  get spareKeyPurchaseEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('spareKey-purchaseEnabled', false);
  }

  /** Accept only; this modal is buyer-only, and hosts without a fee (e.g. the VDP) never show it. */
  get showSpareKey(): boolean {
    return (
      this.spareKeyPurchaseEnabled &&
      this.checkModalType('ACCEPT') &&
      this.platformSpareKeyFee() != null
    );
  }

  /** The option is still shown for a vehicle without a spare key, just disabled. */
  get spareKeyAvailable(): boolean {
    return !!this.bidItem?.hasSpareKey;
  }

  get spareKeyTotal(): number {
    return this.showSpareKey && this.spareKeyAvailable && this.spareKeySelected() ? this.platformSpareKeyFee() : 0;
  }

  get feeDetailsToolTipBody() {
    this.toolTipContent = this._tooltipService.getFeeValues(this.bidItem.buyFee.fees);
    return { body: this.toolTipContent };
  }

  get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isFrenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }
  
  get isInServiceVehiclesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get declineButtonClass() {
    return this.isInServiceVehiclesEnabled ? 'btn btn-primary' : 'btn btn-destructive';
  }

  ngOnInit() {
    this.getTranslations();

    this.form = this._formBuilder.group({
      counterOfferAmount: [''],
      offerComment: ['']
    });

    this.store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(configuration => {
        this.sellingDisclosureUrl = get(configuration, 'sellingDisclosureUrl', null);
      });
  }

  getTranslations() {
    const keyParams = {
      UIComponents: {},
      error: {},
      'common.currency': {},
      'common.labelErrorTitle': {},
      'common.labelErrorSubTitle': {}
    };
    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translatedObj => {
        this.characterCountData = {
          textCharacters: translatedObj[0].textCharacters,
          textEnterMax: translatedObj[0].textEnterMax
        };
        this.textAccept = translatedObj[0].sharedListModalText.accept;
        this.textView = translatedObj[0].sharedListModalText.viewOffer;
        this.textCounter = translatedObj[0].sharedListModalText.counterOffer;
        this.textDecline = translatedObj[0].sharedListModalText.declineOffer;
        this.textPurchaseTotal = translatedObj[0].sharedListModalText.purchasedText;
        this.textSpareKeyTotal = translatedObj[0].sharedListMultiActionModalText.labelSpareKeyTotal;
        this.textAddSpareKeyFor = translatedObj[0].sharedListMultiActionModalText.textAddSpareKeyFor;
        this.textSentToBuyingDealership = translatedObj[0].sharedListMultiActionModalText.textSentToBuyingDealership;
        this.enterValidAmount = translatedObj[1].enterValidAmount;
        this.inputConfig.thousandSeparator = translatedObj[2].thousandSeparator;
        this.labelErrorTitle = translatedObj[3];
        this.labelErrorSubTitle = translatedObj[4];
      });
  }

  getOfferHistory(): Observable<OfferHistory[]> {
    return this.offersService.getOfferHistory$(this.item.auctionItemUuid).pipe(takeUntil(this.destroyed$));
  }

  analyticsInfo(pageDetails: string) {
    this._analyticsService.trackEvent('app-tooltip', pageDetails);
  }

  formatCurrency(currency, withCents?) {
    return this.currencyFormatService.formatCurrency(currency, withCents);
  }

  replaceInvalidCharacters(e) {
    e.target.value = multiLanguageAlphaNumericRegexHelper(e.target.value);
  }

  isFinanceSourceFeeIncluded() {
    return this.financeSource.feeAmount != null && this.financeSource.feeAmount != 0;
  }

  show(modalType: string) {
    this.isOpen = true;
    this.currentModalType = modalType;
    this.spareKeySelected.set(false);
    this.getOfferHistory()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((offers: OfferHistory[]) => {
        this.offerHistory = offers;
        this.offerChain = this.offerHistory && this.offerHistory.length ? this.offerHistory[0] : undefined;
        this.rootOffer = this.offerChain ? this.offerChain.offer.amount : undefined;
        this.formattedRootOffer = this.formatCurrency(this.rootOffer, false);
        this.isLoading = false;
        this.changeDetector.markForCheck();
      });
    setTimeout(() => {
      //timeout is necessary since we are working outside of the Angular ngZone
      this.modal.show();
      this.changeDetector.markForCheck();
    }, 1000);
  }

  checkModalType(modalType: string) {
    return this.currentModalType === modalType;
  }

  close() {
    this.form.reset();
    this.isOpen = false;
    this.modal.close();
    this.errorModal = false;
    this.successfulResponse = false;
    this.waitingResponse = false;
    this.errorMessage = undefined;
    this.spareKeySelected.set(false);
  }

  showAcceptFlags() {
    this.waitingResponse = true;
    this.successfulResponse = false;
    this.acceptedOffer = true;
    this.declinedOffer = false;
    this.errorModal = false;
  }

  accept() {
    const offerComment = this.form.get('offerComment').value;
    this.showAcceptFlags();
    this._offerModalsService
      .handleOfferEvent(
        new AcceptOffer(this.offerHistory[0].offer.offerUuid, this.currentModalType, offerComment),
        this.item,
        this.showSpareKey ? this.spareKeyAvailable && this.spareKeySelected() : undefined
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        response => {
          if (response) {
            this.waitingResponse = false;
            this.successfulResponse = true;
            this.bidItem.buyerPurchaseStatus = 'Offer Accepted';
            this.bidItem.buyerAuctionItemStatus = 'WON';
            this.bidItem.auctionItemStatus = 'SOLD';
            this._analyticsService.fireBuyActionEvent('acceptBuyAction', 1, [this.item.auctionItemUuid]);
            this.changeDetector.markForCheck();
          }
        },
        error => {
          // Generic message at the bottom of the modal; the modal stays open so the buyer can retry
          const errorCode = error?.error?.errors?.[0]?.code;
          if (errorCode) {
            this.analyticsTrackError(errorCode);
          }
          this.errorMessage = `${this.labelErrorTitle} ${this.labelErrorSubTitle}`;
          this.waitingResponse = false;
          this.successfulResponse = false;
          this.acceptedOffer = true;
          this.counteredOffer = true;
          // false renders errorMessage as-is rather than as an error.<code> translation key
          this.isOfferAmount = false;
          this.changeDetector.markForCheck();
        }
      );
  }

  showDeclineFlags() {
    this.waitingResponse = true;
    this.successfulResponse = false;
    this.declinedOffer = true;
    this.acceptedOffer = false;
    this.counteredOffer = false;
  }

  decline() {
    const offerComment = this.form.get('offerComment').value;
    this.showDeclineFlags();
    this._offerModalsService
      .handleOfferEvent(
        new DeclineOffer(this.offerHistory[0].offer.offerUuid, this.currentModalType, offerComment),
        this.item
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        response => {
          if (response) {
            this.waitingResponse = false;
            this.successfulResponse = true;
            this.bidItem.buyerPurchaseStatus = 'Offer Declined';
            this.bidItem.buyerAuctionItemStatus = 'ACCEPTING_OFFERS';
            this._analyticsService.fireBuyActionEvent('declineBuyAction', 1, [this.item.auctionItemUuid]);
            this.changeDetector.markForCheck();
          }
        },
        error => {
          if (error) {
            this.errorMessage = error.error.errors[0].code;
            this.analyticsTrackError(this.errorMessage);
          }
          this.waitingResponse = false;
          this.successfulResponse = false;
          this.declinedOffer = false;
          this.counteredOffer = true;
          this.isOfferAmount = true;
          this.changeDetector.markForCheck();
        }
      );
  }

  analyticsTrackError(errorCode) {
    this._analyticsService.track('customError', BuyErrors.fromBuyErrors(errorCode), errorCode);
  }

  showCounterFlags() {
    this.waitingResponse = true;
    this.successfulResponse = false;
    this.acceptedOffer = false;
    this.declinedOffer = false;
    this.errorMessage = null;
  }

  counter() {
    this.showCounterFlags();
    const offerAmountString = this.form.get('counterOfferAmount').value;
    const offerAmount = parseInt(offerAmountString);
    const offerComment = this.form.get('offerComment').value;

    if (offerAmount) {
      this.waitingResponse = true;
      this._offerModalsService
        .handleOfferEvent(new CounterOffer(offerAmount, offerComment, this.offerHistory[0].offer.offerUuid), this.item)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          response => {
            if (response) {
              this.bidItem.buyerPurchaseStatus = 'Offer Submitted';
              this.bidItem.buyerAuctionItemStatus = 'PENDING_SELLER_ACTION';

              this.waitingResponse = false;
              this.successfulResponse = true;
              this.acceptedOffer = false;
              this.declinedOffer = false;
              this.counteredOffer = true;

              // Merged onto a copy that keeps the item's prototype, so OfferModalItem's getters (titleSimple)
              // survive; a bare object spread copies own fields only and would drop them. Same fields as before.
              this.item = Object.assign(Object.create(Object.getPrototypeOf(this.item)), this.item, response);
              this._analyticsService.fireBuyActionEvent('counterBuyAction', 1, [this.item.auctionItemUuid]);
              this.changeDetector.markForCheck();
            }
          },
          error => {
            if (error) {
              this._offerModalsService.handleOfferError(error);
              this.errorMessage = error.error.errors[0].code;
              this.analyticsTrackError(this.errorMessage);
            }
            this.waitingResponse = false;
            this.counteredOffer = false;
            this.successfulResponse = false;
            this.isOfferAmount = true;
            this.changeDetector.markForCheck();
          }
        );
    } else {
      this.errorMessage = this.enterValidAmount;
      this.waitingResponse = false;
      this.counteredOffer = false;
      this.successfulResponse = false;
      this.isOfferAmount = false;

      // send tracking data
      const inventoryItemUuid = get(this._route, 'snapshot.parent.children[0].params.inventoryItemUuid', null);
      const analyticsPayload = new CounterOffer(offerAmount, offerComment, inventoryItemUuid);

      this._analyticsService.track('customEvent', {
        name: analyticsPayload.type.split('Offer').join('') + ' Offer',
        attr: this._analyticsService.createOfferModalEventAnalyticsPayload(
          analyticsPayload,
          this.errorMessage,
          inventoryItemUuid
        )
      });
    }

    this.form.reset();
  }

  showBuyFeesInOffers(): boolean {
    return this._launchDarklyService.getFeatureFlag('offerModal-feesEnabled', false);
  }

  isSubmittingOffer(): boolean {
    return (
      this.waitingResponse &&
      !this.successfulResponse &&
      !this.acceptedOffer &&
      !this.declinedOffer &&
      !this.counteredOffer
    );
  }

  isSubmittingDecline(): boolean {
    return this.waitingResponse && !this.successfulResponse && !this.acceptedOffer && this.declinedOffer;
  }

  isSubmittingAccept(): boolean {
    return this.waitingResponse && !this.successfulResponse && this.acceptedOffer && !this.declinedOffer;
  }

  isCounterAccepted(): boolean {
    return (
      !this.waitingResponse &&
      this.successfulResponse &&
      !this.acceptedOffer &&
      !this.declinedOffer &&
      this.counteredOffer
    );
  }

  isOfferDeclined(): boolean {
    return !this.waitingResponse && this.successfulResponse && !this.acceptedOffer && this.declinedOffer;
  }

  isOfferAccepted(): boolean {
    return !this.waitingResponse && this.successfulResponse && this.acceptedOffer && !this.declinedOffer;
  }

  isError(): boolean {
    return this.errorModal;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
